/**
 * **Your day** — the landing screen (FR-L5, T-6.19; C14 §10.6 item 6,
 * ADR-0033 §4).
 *
 * A hub of cards over one read, `GET /api/v1/overview` (T-6.20): open cases
 * and tasks due with the late ones counted, today's and this week's filings,
 * unread conversations, unpaid and overdue invoices, new and follow-up-due
 * leads. Each card links to its list and is **present only where the caller
 * holds that resource's `can_view`** — checked here and answered by the
 * service, which narrows every figure as the list it links to narrows (FR-L4).
 * A part the service could not read says so and shows **no figure**.
 *
 * What it kept from the screen it replaced (T-4.14):
 *
 * - **FR-I12's setup checklist** stays above the cards while it is unfinished;
 *   it hides itself once every row is done.
 * - **Nothing invented.** Every figure is the firm's own data; a dashboard of
 *   invented tiles would be believed (docs/DOMAIN_HANDOFF.md §6). A card is
 *   dropped, never disabled, when the person holds no verb on it — a disabled
 *   card advertises a capability they cannot obtain.
 */

import { Alert, EmptyState, PageHeader } from '@fiveox/ui'
import { useEffect, useState } from 'react'

import { HubCard, HubGroup } from '../components/HubCard'
import { SITE, useEvaluation } from '../lib/access'
import { type Overview as OverviewData, overview } from '../lib/api/overview'
import { SetupChecklist } from './Site/SetupChecklist'
import { overviewCards } from './Overview/cards'

/** *Friday, 26 September* — the day the figures are for. */
function dayLabel(isoDate: string | null): string {
  const when = isoDate ? new Date(`${isoDate}T00:00:00`) : new Date()
  return when.toLocaleDateString('en-IN', { weekday: 'long', day: 'numeric', month: 'long' })
}

export function Overview() {
  const { can, evaluation } = useEvaluation()
  const [data, setData] = useState<OverviewData | null>(null)
  const [asAt, setAsAt] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    let cancelled = false
    overview
      .read()
      .then((body) => {
        if (cancelled) return
        setData(body.data)
        const meta = body.meta as { as_at?: string } | undefined
        setAsAt(meta?.as_at ?? null)
      })
      .catch(() => !cancelled && setFailed(true))
      .finally(() => !cancelled && setLoading(false))
    return () => {
      cancelled = true
    }
  }, [])

  const groups = overviewCards({ can, data, loading: loading || !evaluation, failed })

  return (
    <div className="mx-auto w-full max-w-6xl space-y-8">
      <PageHeader title="Your day" description={`What needs you on ${dayLabel(asAt)}.`} />

      {/* FR-I12. Hides itself once every row is done. */}
      {can(SITE, 'can_view') && <SetupChecklist />}

      {failed && (
        <Alert tone="warning">
          We couldn’t count your day just now. Every card still opens its list — the figures will be
          back when the read is.
        </Alert>
      )}

      {groups.map((group) => (
        <HubGroup
          key={group.key}
          id={`overview-${group.key}`}
          title={group.title}
          description={group.description}
          columns={group.cards.length % 3 === 0 || group.cards.length === 1 ? 3 : 2}
        >
          {group.cards.map((card) => (
            <HubCard
              key={card.key}
              cardKey={card.key}
              href={card.href}
              icon={card.icon}
              title={card.title}
              description={card.description}
              loading={card.line === null}
              line={card.line}
            />
          ))}
        </HubGroup>
      ))}

      {evaluation && groups.length === 0 && (
        <EmptyState
          title="Nothing to count for you here"
          description="Your role doesn’t include cases, the calendar, messages, invoices or leads. Use the menu for the parts of ProDesk you work in, or ask a partner for access."
        />
      )}
    </div>
  )
}
